import React from 'react';
import { addDays, format } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { DisplayTimes } from '../../..';
import { fetchDateSlots, fetchSlotsByDate, SlotHour } from '../../../../../modules/slots';
import { dateToISoString } from '../../../../../utils/dates';
import { toast } from 'react-toastify';
import { EavSlotsProps, ScheduleItemProps } from '../../../../../models/Schedule';
import { Rule } from '../../../../../models/Product';

type useSGHSlotsProps = {
  selectedItemToSchedule: string;
  setDisplayDates: (value: Date[]) => void;
  setDisplayDatesPicker: (value: React.SetStateAction<Date[] | undefined>) => void;
  setLoading: (value: boolean) => void;
  setSelectedDate: (value: Date | null) => void;
  addSlotTimes: (scheduleSlots: DisplayTimes[]) => void;
  findUnitDatesAvailable: (scheduleId: string, unitId: string) => string[];
  schedule: ScheduleItemProps | undefined;
  setSelectedTime: (value: DisplayTimes | undefined) => void;
  eavSlotsData: EavSlotsProps | undefined;
  scheduleForRemove: any;
  scheduleDateToRemove: any;
  scheduleHourToRemove: any;
  productRules: Rule[];
};

export default function useSGHSlots({
  selectedItemToSchedule,
  setDisplayDates,
  setDisplayDatesPicker,
  setLoading,
  setSelectedDate,
  addSlotTimes,
  findUnitDatesAvailable,
  schedule,
  setSelectedTime,
  eavSlotsData,
  scheduleForRemove,
  scheduleDateToRemove,
  scheduleHourToRemove,
  productRules
}: useSGHSlotsProps) {
  const getDatesToDisplay = (availableDates: string[]) => {
    return availableDates.map(dateString => {
      return new Date(dateString + 'T00:00:00');
    });
  };

  const loadSGHSlotsMonth = async ({
    unitIdSGH,
    itemId,
    isKit,
    date,
    resourceId,
    changeMonth,
    localId
  }: {
    unitIdSGH: string;
    itemId: string;
    isKit: boolean;
    date: Date;
    resourceId: string | number | null;
    changeMonth?: boolean;
    localId: string;
  }) => {
    try {
      setLoading(true);
      if (!changeMonth) setSelectedDate(date);
      const startDate = new Date();
      if (date.getMonth() > startDate.getMonth()) {
        startDate.setDate(1);
        startDate.setMonth(date.getMonth());
        startDate.setFullYear(date.getFullYear());
      }
      const endDate = format(addDays(startDate, 31), 'yyyy-MM-dd');

      const unitTimes = await fetchDateSlots({
        unitId: unitIdSGH,
        itemId,
        isKit,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate,
        resourceId,
        localId
      });
      const displayDates = getDatesToDisplay(unitTimes.slotDates);
      setDisplayDatesPicker(displayDates);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Falha ao carregar dados. Tente novamente mais tarde.');
    }
  };

  const handleSelectedDate = (date: Date) => {
    if (schedule?.unitId && schedule?.itemId)
      loadSGHSlotsMonth({
        unitIdSGH: schedule.unitId,
        itemId: schedule.itemId,
        isKit: schedule.isKit || false,
        date: date,
        resourceId: schedule.useResouce ? schedule?.resource?.id || null : null,
        localId: schedule.localId || ''
      });
  };

  function makeDisplayDate(stringDate: string, slots: SlotHour[]) {
    const dateTime = new Date(stringDate);
    return {
      id: uuid(),
      time: dateTime,
      slots: slots
    };
  }

  const getSlotsByDate = async ({
    unitId,
    itemId,
    isKit,
    date,
    resourceId,
    localId
  }: {
    unitId: string;
    itemId: string;
    isKit: boolean;
    date: Date;
    resourceId: string | number | null;
    localId: string;
  }) => {
    setSelectedTime(undefined);
    if (schedule?.careType === 'EINSTEIN_TO_YOU') {
      if (!eavSlotsData) return;
      const displayEavDates = Object.keys(eavSlotsData?.availableSchedules);
      const formatedHours = displayEavDates
        .filter(key => key === format(date, 'yyyy-MM-dd'))
        .map(key => {
          return eavSlotsData?.availableSchedules[key].map(({ intervalStart }) => {
            const dateFormat = new Date(intervalStart);

            return {
              id: uuid(),
              slots: [
                {
                  date: key,
                  hour: `${dateFormat.getHours()}:${dateFormat.getMinutes()}`
                }
              ],
              time: dateFormat
            } as DisplayTimes;
          });
        })
        .flat(1);

      if (!formatedHours.length) toast.warning('Essa data não possui horários disponíveis. Selecione outra data.');

      const orderedList = formatedHours.sort((time, time2) => {
        if (time.time > time2.time) return 1;
        else return -1;
      });

      addSlotTimes(orderedList);
      setLoading(false);
    } else {
      handleSelectedDate(date);
      setLoading(true);
      const dateString = dateToISoString(date);
      try {
        const slots = await fetchSlotsByDate({
          unitId,
          itemId,
          isKit,
          date: dateString,
          resourceId,
          localId
        });

        const displayDates = slots.map(slot => {
          const displayDate = makeDisplayDate(`${slot.date}T${slot.hour}`, [slot]);
          return displayDate;
        });

        if (!displayDates.length) toast.warning('Essa data não possui horários disponíveis. Selecione outra data.');

        const filteredList = displayDates.filter(dates =>
          scheduleForRemove
            ? dates.slots[0].date === scheduleDateToRemove
              ? dates.slots[0].hour !== scheduleHourToRemove
              : dates
            : dates
        );

        const orderedList = filteredList.sort((time, time2) => {
          if (time.time > time2.time) return 1;
          else return -1;
        });

        if (schedule) addSlotTimes(orderedList);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    }
  };

  const onSelectDateSGH = (date: Date) => {
    if (
      selectedItemToSchedule === 'PRODUCT' ||
      selectedItemToSchedule === 'PRODUCT_ENV' ||
      selectedItemToSchedule === 'SERVICE_ENV'
    )
      getSlotsByDate({
        unitId: schedule?.unitId || '',
        itemId: schedule?.itemId || '',
        isKit: schedule?.isKit || false,
        date,
        resourceId: schedule?.useResouce ? schedule?.resource?.id || null : null,
        localId: schedule?.localId || ''
      });
  };

  React.useEffect(() => {
    if (
      selectedItemToSchedule === 'PRODUCT' ||
      selectedItemToSchedule === 'PRODUCT_ENV' ||
      selectedItemToSchedule === 'SERVICE_ENV'
    ) {
      if (schedule?.unitId) {
        const dates = findUnitDatesAvailable(schedule.id, schedule.unitId + schedule.localId ?? '');
        if (dates) {
          const displayDates = getDatesToDisplay(dates);
          setDisplayDates(displayDates.slice(0, 3));
          setDisplayDatesPicker(displayDates);

          const [firstDate] = displayDates;
          if (firstDate) {
            getSlotsByDate({
              unitId: schedule?.unitId || '',
              itemId: schedule?.itemId || '',
              isKit: schedule?.isKit || false,
              date: firstDate,
              resourceId: schedule.useResouce ? schedule?.resource?.id?.toString() || null : null,
              localId: schedule.localId || ''
            });
          }
        }
      }
      if (schedule?.careType === 'EINSTEIN_TO_YOU') {
        if (!eavSlotsData) return;
        const displayEavDates = Object.keys(eavSlotsData?.availableSchedules);
        const displayDates = getDatesToDisplay(displayEavDates);
        setDisplayDates(displayDates.slice(0, 3));
        setDisplayDatesPicker(displayDates);

        const firstDate = displayDates[0];
        getSlotsByDate({
          isKit: false,
          itemId: '',
          resourceId: null,
          date: firstDate,
          unitId: '',
          localId: ''
        });
      }
    }
  }, []);

  return { onSelectDateSGH, loadSGHSlotsMonth };
}
